import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import WarningIconSmall from 'assets/svgs/warningSmallIcon.svg';
import { AlertNotice } from 'shared/components/index';
import './ExamineeStatus.scss';
import PropTypes from 'prop-types';

const ExamineeStatus = ({ statusSummary, examinees, eventLevelFlag }) => {
  const { t } = useTranslation();
  const hasUnknown = statusSummary.unresolved > 0 || statusSummary.unknown > 0;
  const unknownClass = hasUnknown ? 'unknown-item' : '';
  const hasNoSeating = hasUnknown && statusSummary.tested === 0;
  const hasExpanded = examinees.length > 5;
  const filteredNames = hasExpanded ? examinees.filter((name, index) => index < 5) : examinees;
  const extraNames = examinees.length - 5;
  const statusClass = hasUnknown ? 'unknown-status' : '';
  const [displayedNames, setDisplayedNames] = useState({
    filteredNames: [],
    seeMore: true,
  });
  const noSeatingText = eventLevelFlag
    ? t('tcEventDashboard.checkout.examinees.noSeating')
    : t('reviewAndCloseRoom.examineeStatus.noSeating');

  useEffect(() => {
    if (examinees.length) {
      setDisplayedNames({
        ...displayedNames,
        filteredNames,
      });
    }
  }, [examinees]);

  useEffect(() => {
    Array.from(document.querySelectorAll('[data-bs-toggle="popover"]')).forEach((el) =>
      bootstrap.Popover.getOrCreateInstance(el).enable()
    );
  }, []);

  const seeMore = () => {
    if (displayedNames.seeMore) {
      setDisplayedNames({
        filteredNames: examinees,
        seeMore: false,
      });
    }
    if (!displayedNames.seeMore) {
      setDisplayedNames({
        filteredNames,
        seeMore: true,
      });
    }
  };

  return (
    <div className="examinee-status-container">
      <h4>{t('reviewAndCloseRoom.examineeStatus.title')}</h4>
      <div className="examinee-status-content">
        <div className={`examinee-status-summary ${statusClass}`}>
          <h5>{t('reviewAndCloseRoom.examineeStatus.summary')}</h5>
          <div className="summary-item-container">
            <div className="summary-list-item">
              <div>{t('reviewAndCloseRoom.examineeStatus.tested')}</div>
              <div>{statusSummary.tested}</div>
            </div>
            <div className="summary-list-item">
              <div>{t('reviewAndCloseRoom.examineeStatus.denied')}</div>
              <div>{statusSummary.deniedAdmission}</div>
            </div>
            <div className="summary-list-item">
              <div>{t('reviewAndCloseRoom.examineeStatus.absent')}</div>
              <div>{statusSummary.absent}</div>
            </div>
            <div
              className={`summary-list-item ${unknownClass}`}
              data-bs-toggle={hasUnknown ? 'popover' : ''}
              data-bs-placement="top"
              data-bs-trigger="hover"
              data-bs-html
              data-bs-template='<div class="popover unknown-tooltip" role="tooltip"><div class="arrow"></div><div class="popover-body"></div></div>'
              data-bs-content={
                eventLevelFlag
                  ? t('tcEventDashboard.checkout.examinees.unknownTooltip')
                  : t('reviewAndCloseRoom.examineeStatus.unknownTooltip')
              }>
              <div>
                {hasUnknown && <WarningIconSmall />}
                <div>{t('reviewAndCloseRoom.examineeStatus.unknown')}</div>
              </div>
              <div>{statusSummary.unresolved || statusSummary.unknown}</div>
            </div>
          </div>
          <div className="summary-list-item total">
            <div>
              {!eventLevelFlag
                ? t('reviewAndCloseRoom.examineeStatus.totalAssigned')
                : t('tcEventDashboard.checkout.examinees.totalAssigned')}
            </div>
            <div>{statusSummary.total}</div>
          </div>
        </div>
        {hasUnknown && (
          <div className="unknown-status-container">
            <AlertNotice
              type="warning"
              message={
                hasNoSeating ? noSeatingText : t('reviewAndCloseRoom.examineeStatus.statusWarning')
              }
            />
            {!hasNoSeating && (
              <>
                <div className="unknown-list-title">
                  {t('reviewAndCloseRoom.examineeStatus.examineesUnknown')}
                </div>
                <ul>
                  {displayedNames.filteredNames.map((name, i) => (
                    <li
                      key={`examinee-${i}`}
                      className="unknown-list-item">{`${name.lastName}, ${name.firstName}`}</li>
                  ))}
                </ul>
                {hasExpanded && (
                  <div className="see-more" onClick={seeMore}>
                    {displayedNames.seeMore ? `See ${extraNames} more` : 'See less'}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
      {!hasUnknown && (
        <AlertNotice
          message={t('reviewAndCloseRoom.examineeStatus.allExaminees')}
          type="success"
          noBorder
        />
      )}
    </div>
  );
};

export default ExamineeStatus;

ExamineeStatus.propTypes = {
  statusSummary: PropTypes.shape({
    tested: PropTypes.number,
    deniedAdmission: PropTypes.number,
    absent: PropTypes.number,
    unresolved: PropTypes.number,
    unknown: PropTypes.number,
    total: PropTypes.number,
  }).isRequired,
  eventLevelFlag: PropTypes.bool,
  examinees: PropTypes.arrayOf(
    PropTypes.shape({
      lastName: PropTypes.string,
      firstName: PropTypes.string,
    })
  ).isRequired,
};

ExamineeStatus.defaultProps = {
  eventLevelFlag: false,
};
